import { useGetAuditLogFilterOptions, useSearchAuditLogs } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Input from '@components/V4/Inputs/Input';
import Pagination from '@components/V4/Pagination';
import Select from '@components/V4/Select/Select';
import {
  EmptyState,
  FilterField,
  SectionHeading,
  TableShell,
} from '@modules/Admin/Reporting/common';
import {
  AuditLogEntryDTO,
  AuditLogFilters,
  AuditLogOperationDTO,
} from '@shared/apis/auditLog.apis';
import useDebounce from 'apps/agora/src/hooks/useDebounce';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { useState } from 'react';

const VISIBLE_CHANGES = 4;

const headRow = 'border-b border-white/10 text-xs uppercase tracking-wide text-customGrey';
const bodyRow = 'border-b border-white/5 align-top last:border-b-0';
const leftHead = 'px-5 py-3 text-left font-semibold';
const leftCell = 'px-5 py-3 text-left text-customLight';

const OPERATION_LABELS: Record<AuditLogOperationDTO, string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  bulk: 'Bulk change',
};

const OPERATION_STYLES: Record<AuditLogOperationDTO, string> = {
  create: 'bg-emerald-500/15 text-emerald-300',
  update: 'bg-sky-500/15 text-sky-300',
  delete: 'bg-red-500/15 text-red-300',
  bulk: 'bg-amber-500/15 text-amber-300',
};

const OPERATION_OPTIONS = (Object.keys(OPERATION_LABELS) as AuditLogOperationDTO[]).map(
  (value) => ({ value, label: OPERATION_LABELS[value] })
);

const ChangeList = ({ entry }: { entry: AuditLogEntryDTO }) => {
  const [expanded, setExpanded] = useState(false);
  const changes = expanded ? entry.changes : entry.changes.slice(0, VISIBLE_CHANGES);

  if (entry.summary) {
    return <span className="text-customLight">{entry.summary}</span>;
  }

  if (!entry.changes.length) {
    return <span className="text-customGrey">No field details</span>;
  }

  return (
    <div className="flex flex-col gap-1.5">
      {changes.map((change, index) => (
        <div key={`${change.path}-${index}`} className="text-xs">
          <span className="font-mono text-customLight">{change.path}</span>
          <div className="flex flex-wrap items-center gap-1.5">
            {change.kind !== 'added' ? (
              <span className="break-all text-red-300 line-through">{change.from ?? '—'}</span>
            ) : null}
            {change.kind === 'set' ? <span className="text-customGrey">→</span> : null}
            {change.kind !== 'removed' ? (
              <span className="break-all text-emerald-300">{change.to ?? '—'}</span>
            ) : null}
          </div>
        </div>
      ))}
      {entry.changes.length > VISIBLE_CHANGES ? (
        <button
          type="button"
          className="self-start text-xs text-customPrimary"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? 'Show less' : `Show all ${entry.changes.length} changes`}
        </button>
      ) : null}
      {entry.truncated ? (
        <span className="text-xs text-customGrey">Only the first changes were recorded.</span>
      ) : null}
    </div>
  );
};

const AuditLog = () => {
  const [filters, setFilters] = useState<AuditLogFilters>({});
  const [pathSearch, setPathSearch] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const debouncedPath = useDebounce(pathSearch, 400);
  const debouncedDocumentId = useDebounce(documentId, 400);

  const { data: options } = useGetAuditLogFilterOptions();

  const params: AuditLogFilters = {
    ...filters,
    path: debouncedPath.trim() || undefined,
    documentId: debouncedDocumentId.trim() || undefined,
    page,
    limit,
  };

  const { data, isLoading, isFetching } = useSearchAuditLogs(params, { keepPreviousData: true });

  const updateFilters = (patch: Partial<AuditLogFilters>) => {
    setFilters((previous) => ({ ...previous, ...patch }));
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-8 p-8 font-raleway text-white">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold">Audit Log</h1>
        <p className="max-w-3xl text-sm text-customGrey">
          Every change an admin made in Agora, field by field. Changes made by mentors, students,
          parents and automatic jobs are not recorded. Bulk operations are shown as a summary.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <FilterField width="tablet:w-64">
          <Select
            variant="secondary"
            label="Admin"
            showSearch
            optionFilterProp="label"
            placeholder="Any admin"
            value={filters.actorId}
            options={(options?.actors ?? []).map((actor) => ({
              value: actor._id,
              label: actor.name,
            }))}
            onChange={(value) => updateFilters({ actorId: (value as string) ?? undefined })}
          />
        </FilterField>
        <FilterField width="tablet:w-52">
          <Select
            variant="secondary"
            label="Object type"
            showSearch
            placeholder="Any type"
            value={filters.collectionName}
            options={(options?.collections ?? []).map((collection) => ({
              value: collection,
              label: collection,
            }))}
            onChange={(value) => updateFilters({ collectionName: (value as string) ?? undefined })}
          />
        </FilterField>
        <FilterField width="tablet:w-44">
          <Select
            variant="secondary"
            label="Operation"
            placeholder="Any"
            value={filters.operation}
            options={OPERATION_OPTIONS}
            onChange={(value) =>
              updateFilters({ operation: (value as AuditLogOperationDTO) ?? undefined })
            }
          />
        </FilterField>
        <FilterField width="tablet:w-56">
          <Input
            type="text"
            name="audit-path"
            id="audit-path"
            label="Field"
            placeholder="e.g. mentorData.rate"
            value={pathSearch}
            onChange={(event) => {
              setPathSearch(event.target.value);
              setPage(1);
            }}
          />
        </FilterField>
        <FilterField width="tablet:w-60">
          <Input
            type="text"
            name="audit-document"
            id="audit-document"
            label="Object ID"
            placeholder="Exact id"
            value={documentId}
            onChange={(event) => {
              setDocumentId(event.target.value);
              setPage(1);
            }}
          />
        </FilterField>
        <FilterField width="tablet:w-44">
          <Input
            type="date"
            name="audit-from"
            id="audit-from"
            label="From"
            value={filters.from ?? ''}
            onChange={(event) => updateFilters({ from: event.target.value || undefined })}
          />
        </FilterField>
        <FilterField width="tablet:w-44">
          <Input
            type="date"
            name="audit-to"
            id="audit-to"
            label="To"
            value={filters.to ?? ''}
            onChange={(event) => updateFilters({ to: event.target.value || undefined })}
          />
        </FilterField>
      </div>

      <section className="flex flex-col gap-4">
        <SectionHeading
          title={`Changes${data ? ` (${data.total})` : ''}`}
          description="Passwords, tokens and activity timestamps are never recorded."
        />

        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : !data?.entries.length ? (
          <EmptyState>No admin changes match these filters.</EmptyState>
        ) : (
          <div className={mergeClassNames('flex flex-col gap-4', isFetching ? 'opacity-60' : '')}>
            <TableShell minWidth="min-w-[1100px]">
              <thead>
                <tr className={headRow}>
                  <th className={leftHead}>Date</th>
                  <th className={leftHead}>Admin</th>
                  <th className={leftHead}>Object</th>
                  <th className={leftHead}>Operation</th>
                  <th className={leftHead}>Changes</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((entry) => (
                  <tr key={entry._id} className={bodyRow}>
                    <td className={mergeClassNames(leftCell, 'whitespace-nowrap')}>
                      {moment(entry.createdAt).format('DD MMM YYYY, HH:mm:ss')}
                    </td>
                    <td className={leftCell}>
                      <div>{entry.actor.name ?? 'Unknown'}</div>
                      {entry.actor.email ? (
                        <div className="text-xs text-customGrey">{entry.actor.email}</div>
                      ) : null}
                    </td>
                    <td className={mergeClassNames(leftCell, 'max-w-[280px]')}>
                      <div className="font-semibold">{entry.collectionName}</div>
                      {entry.documentLabel ? (
                        <div className="text-xs text-customLight">{entry.documentLabel}</div>
                      ) : null}
                      {entry.documentId ? (
                        <button
                          type="button"
                          className="break-all text-left font-mono text-[11px] text-customGrey hover:text-customPrimary"
                          title="Show all changes of this object"
                          onClick={() => {
                            setDocumentId(entry.documentId ?? '');
                            setPage(1);
                          }}
                        >
                          {entry.documentId}
                        </button>
                      ) : null}
                      {entry.request?.path ? (
                        <div className="mt-1 break-all text-[11px] text-customGrey">
                          {entry.request.method} {entry.request.path}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={mergeClassNames(
                          'whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold',
                          OPERATION_STYLES[entry.operation]
                        )}
                      >
                        {OPERATION_LABELS[entry.operation]}
                      </span>
                    </td>
                    <td className="max-w-[460px] px-5 py-3">
                      <ChangeList entry={entry} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableShell>

            <Pagination
              pageSize={limit}
              currentPage={page}
              total={data.total}
              onCurrentPageChange={setPage}
              onPageSizeChange={(size) => {
                setLimit(size);
                setPage(1);
              }}
            />
          </div>
        )}
      </section>
    </div>
  );
};

export default AuditLog;
